import "../features/eex/operations-pages.css";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Activity,
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  ClipboardPaste,
  CopyPlus,
  Database,
  LineChart,
  Plus,
  RefreshCw,
  Save,
  Trash2,
  WandSparkles,
  X,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useEexRole } from "../components/eex/use-eex-role";
import { api, ApiError } from "../lib/api";
import {
  EUR_RON_PAIR,
  addMonth,
  compareFxCurves,
  currentMonth,
  fillMissingFxForwardMonths,
  isoToday,
  normalizeFxActuals,
  normalizeFxCurveDetail,
  normalizeFxCurveList,
  normalizeFxEffective,
  parseFxCurvePaste,
  type FxCurveDetail,
  type FxCurveComparison,
  type FxCurveHeader,
  type FxEffectiveRow,
  type FxForwardPoint,
  type FxForwardPointDerivation,
} from "../lib/eex-fx";
import { cn } from "../lib/utils";
import { desktopOnlyRoute } from "../components/ui/mobile-availability-page";

export const Route = createFileRoute("/eex/fx")({
  component: desktopOnlyRoute(EexFxPage),
});

type Tab = "forward" | "actuals" | "effective";

function EexFxPage() {
  const { canWrite } = useEexRole();
  const [tab, setTab] = useState<Tab>("effective");

  return (
    <div className="operations-workspace ops-page ops-fx space-y-5 p-4">
      <header className="ops-heading flex flex-wrap items-end gap-3">
        <div className="mr-auto">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-lg font-semibold">FX market data</h1>
            <ConventionBadge />
          </div>
          <p className="mt-1 max-w-4xl text-xs text-muted">
            Manage confirmed forward snapshots, inspect BNR historical actuals, and see the rate selected for each delivery month.
            Quotes use the mid from the latest confirmed curve and freeze that snapshot when saved.
          </p>
        </div>
      </header>

      <div className="ops-fx-tabs flex w-fit rounded-md border border-border/70 p-0.5 text-xs" role="tablist" aria-label="FX market data views">
        <TabButton active={tab === "effective"} onClick={() => setTab("effective")}>Effective FX</TabButton>
        <TabButton active={tab === "forward"} onClick={() => setTab("forward")}>Curve snapshots</TabButton>
        <TabButton active={tab === "actuals"} onClick={() => setTab("actuals")}>BNR history</TabButton>
      </div>

      {tab === "effective" && <EffectiveRates onManageCurves={() => setTab("forward")} />}
      {tab === "forward" && <ForwardCurves canWrite={canWrite} />}
      {tab === "actuals" && <Actuals />}
    </div>
  );
}

function ConventionBadge() {
  return (
    <span className="rounded-full border border-accent/40 bg-accent/5 px-2 py-0.5 font-mono text-[11px] text-accent">
      EUR/RON · RON per EUR
    </span>
  );
}

function TabButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={cn(
        "rounded px-3 py-1.5 transition-colors",
        active ? "bg-accent text-white" : "text-muted hover:bg-border/30 hover:text-fg"
      )}
    >
      {children}
    </button>
  );
}

interface PointForm {
  deliveryMonth: string;
  bid: string;
  mid: string;
  ask: string;
  derivation?: FxForwardPointDerivation;
}

type PointEditableKey = "deliveryMonth" | "bid" | "mid" | "ask";
const MAX_CURVE_POINTS = 240;

interface CurveForm {
  curveDate: string;
  source: string;
  notes: string;
  points: PointForm[];
}

function emptyCurveForm(): CurveForm {
  return {
    curveDate: isoToday(),
    source: "",
    notes: "",
    points: [{ deliveryMonth: currentMonth(), bid: "", mid: "", ask: "" }],
  };
}

function formOf(curve: FxCurveDetail): CurveForm {
  return {
    curveDate: curve.curveDate,
    source: curve.source,
    notes: curve.notes ?? "",
    points: curve.points.map((point) => ({
      deliveryMonth: point.deliveryMonth,
      bid: point.bid == null ? "" : String(point.bid),
      mid: String(point.mid),
      ask: point.ask == null ? "" : String(point.ask),
      ...(point.derivation ? { derivation: point.derivation } : {}),
    })),
  };
}

function ForwardCurves({ canWrite }: { canWrite: boolean }) {
  const queryClient = useQueryClient();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState<CurveForm>(() => emptyCurveForm());
  const [dirty, setDirty] = useState(false);
  const [formVersion, setFormVersion] = useState<number | null>(null);
  const [paste, setPaste] = useState("");
  const [pasteErrors, setPasteErrors] = useState<string[]>([]);
  const [notice, setNotice] = useState<{ tone: "ok" | "error" | "muted"; text: string } | null>(null);
  const [confirmReviewOpen, setConfirmReviewOpen] = useState(false);

  const curvesQuery = useQuery({
    queryKey: ["eex-fx-curves", EUR_RON_PAIR],
    queryFn: async () => normalizeFxCurveList(await api<unknown>(`/eex/fx/curves?pair=${encodeURIComponent(EUR_RON_PAIR)}`)),
  });
  const curves = curvesQuery.data ?? [];

  useEffect(() => {
    if (creating || selectedId || curves.length === 0) return;
    setSelectedId(curves[0]!.id);
  }, [creating, curves, selectedId]);

  const detailQuery = useQuery({
    queryKey: ["eex-fx-curve", selectedId],
    enabled: !creating && selectedId != null,
    queryFn: async () => {
      const curve = normalizeFxCurveDetail(await api<unknown>(`/eex/fx/curves/${selectedId}`));
      if (!curve) throw new Error("The curve response was incomplete.");
      return curve;
    },
  });
  const selectedCurve = detailQuery.data ?? null;
  const comparisonAsOf = /^\d{4}-\d{2}-\d{2}$/.test(form.curveDate)
    ? form.curveDate
    : isoToday();
  const activeCurveQuery = useQuery({
    queryKey: ["eex-fx-latest", EUR_RON_PAIR, comparisonAsOf],
    enabled:
      canWrite &&
      /^\d{4}-\d{2}-\d{2}$/.test(form.curveDate) &&
      (creating || selectedCurve?.status === "draft"),
    queryFn: () =>
      api<unknown>(
        `/eex/fx/curves/latest?pair=${encodeURIComponent(EUR_RON_PAIR)}&asOf=${comparisonAsOf}`
      ).then(normalizeFxCurveDetail),
  });

  useEffect(() => {
    if (creating || !selectedCurve || dirty) return;
    setForm(formOf(selectedCurve));
    setFormVersion(selectedCurve.version);
    setDirty(false);
    setPaste("");
    setPasteErrors([]);
  }, [creating, selectedCurve, dirty]);

  const refresh = async (curve?: FxCurveDetail | null) => {
    await queryClient.invalidateQueries({ queryKey: ["eex-fx-curves"] });
    await queryClient.invalidateQueries({ queryKey: ["eex-fx-latest"] });
    if (curve) {
      queryClient.setQueryData(["eex-fx-curve", curve.id], curve);
      setSelectedId(curve.id);
      setCreating(false);
      setForm(formOf(curve));
      setFormVersion(curve.version);
      setDirty(false);
    } else if (selectedId) {
      await queryClient.invalidateQueries({ queryKey: ["eex-fx-curve", selectedId] });
    }
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      const points = pointsOf(form);
      const payload = {
        pair: EUR_RON_PAIR,
        curveDate: form.curveDate,
        source: form.source.trim(),
        notes: form.notes.trim() || null,
        points: points.map((point) => ({
          deliveryMonth: point.deliveryMonth,
          bid: point.bid,
          mid: point.mid,
          ask: point.ask,
          provenance: point.derivation ?? { kind: "market_input" as const },
        })),
      };
      const response = creating
        ? await api<unknown>("/eex/fx/curves", { method: "POST", body: JSON.stringify(payload) })
        : await api<unknown>(`/eex/fx/curves/${selectedCurve!.id}`, {
            method: "PUT",
            body: JSON.stringify({ ...payload, version: formVersion ?? selectedCurve!.version }),
          });
      const curve = normalizeFxCurveDetail(response);
      if (!curve) throw new Error("The saved curve response was incomplete.");
      return curve;
    },
    onSuccess: async (curve) => {
      await refresh(curve);
      setNotice({ tone: "ok", text: `Draft saved as version ${curve.version}.` });
    },
    onError: (error) => setNotice({ tone: "error", text: mutationError(error) }),
  });

  const confirmMutation = useMutation({
    mutationFn: async (curve: FxCurveDetail) => {
      const response = await api<unknown>(`/eex/fx/curves/${curve.id}/confirm`, {
        method: "POST",
        body: JSON.stringify({ version: curve.version }),
      });
      const confirmed = normalizeFxCurveDetail(response);
      if (!confirmed) throw new Error("The confirmation response was incomplete.");
      return confirmed;
    },
    onSuccess: async (curve) => {
      await refresh(curve);
      setConfirmReviewOpen(false);
      setNotice({ tone: "ok", text: `Confirmed version ${curve.version}. New Quotes can now consume this curve.` });
    },
    onError: (error) => setNotice({ tone: "error", text: mutationError(error) }),
  });

  const startNew = (from?: FxCurveDetail | null) => {
    if (saveMutation.isPending || confirmMutation.isPending) return;
    if (dirty && !window.confirm("Discard the unsaved curve changes?")) return;
    setSelectedId(null);
    setCreating(true);
    setFormVersion(null);
    setForm(from ? { ...formOf(from), curveDate: isoToday() } : emptyCurveForm());
    setDirty(false);
    setPaste("");
    setPasteErrors([]);
    setNotice(null);
    setConfirmReviewOpen(false);
  };

  const selectCurve = (curve: FxCurveHeader) => {
    if (saveMutation.isPending || confirmMutation.isPending) return;
    if (dirty && !window.confirm("Discard the unsaved curve changes?")) return;
    setCreating(false);
    setDirty(false);
    setFormVersion(null);
    setSelectedId(curve.id);
    setNotice(null);
    setConfirmReviewOpen(false);
  };

  const edit = <K extends keyof CurveForm>(key: K, value: CurveForm[K]) => {
    setForm((current) => ({ ...current, [key]: value }));
    setDirty(true);
  };

  const editPoint = (index: number, key: PointEditableKey, value: string) => {
    setForm((current) => ({
      ...current,
      points: editPointAndInvalidateDependants(current.points, index, key, value),
    }));
    setDirty(true);
  };

  const validationIssue = curveFormIssue(form);
  const editable = canWrite && (creating || selectedCurve?.status === "draft");
  const comparison = useMemo<FxCurveComparison | null>(() => {
    if (validationIssue) return null;
    return compareFxCurves(pointsOf(form), activeCurveQuery.data?.points ?? []);
  }, [activeCurveQuery.data?.points, form, validationIssue]);

  return (
    <div className="ops-fx-workbench grid min-h-[34rem] gap-4 lg:grid-cols-[18rem_minmax(0,1fr)]">
      <aside className="ops-panel ops-fx-snapshots overflow-hidden rounded-md border border-border/70">
        <div className="ops-fx-section-heading flex items-center gap-2 border-b border-border/70 px-3 py-2">
          <div>
            <div className="text-xs font-semibold">Curve snapshots</div>
            <div className="text-[10px] text-muted">Drafts and immutable confirmations</div>
          </div>
          {canWrite && (
            <button type="button" disabled={saveMutation.isPending || confirmMutation.isPending} onClick={() => startNew()} className="ml-auto inline-flex items-center gap-1 rounded border border-accent/50 px-2 py-1 text-[11px] text-accent hover:bg-accent/10 disabled:opacity-40">
              <Plus className="h-3 w-3" /> New
            </button>
          )}
        </div>
        <div className="max-h-[42rem] overflow-y-auto">
          {curvesQuery.isLoading && <EmptyText>Loading curves…</EmptyText>}
          {curvesQuery.isError && <ErrorText>{mutationError(curvesQuery.error)}</ErrorText>}
          {!curvesQuery.isLoading && curves.length === 0 && <EmptyText>No curves have been created yet.</EmptyText>}
          {curves.map((curve) => (
            <button
              type="button"
              key={curve.id}
              disabled={saveMutation.isPending || confirmMutation.isPending}
              onClick={() => selectCurve(curve)}
              className={cn(
                "block w-full border-b border-border/50 px-3 py-2 text-left text-xs last:border-b-0 hover:bg-border/15",
                !creating && selectedId === curve.id && "bg-accent/5"
              )}
            >
              <div className="flex items-center gap-2">
                <span className="font-mono font-medium">{curve.curveDate}</span>
                <StatusBadge status={curve.status} />
                <span className="ml-auto font-mono text-[10px] text-muted">v{curve.version}</span>
              </div>
              <div className="mt-1 truncate text-[11px] text-muted" title={curve.source}>{curve.source}</div>
            </button>
          ))}
        </div>
      </aside>

      <section className="ops-panel ops-fx-editor min-w-0 overflow-hidden rounded-md border border-border/70">
        {detailQuery.isLoading && !creating ? (
          <EmptyText>Loading curve…</EmptyText>
        ) : detailQuery.isError && !creating ? (
          <ErrorText>{mutationError(detailQuery.error)}</ErrorText>
        ) : !creating && !selectedCurve ? (
          <EmptyText>Select a curve, or create a new draft.</EmptyText>
        ) : (
          <>
            <div className="ops-fx-section-heading flex flex-wrap items-center gap-2 border-b border-border/70 px-4 py-3">
              <div className="mr-auto">
                <div className="flex items-center gap-2">
                  <h2 className="text-sm font-semibold">{creating ? "New forward-curve draft" : `Forward curve · ${selectedCurve!.curveDate}`}</h2>
                  {!creating && <StatusBadge status={selectedCurve!.status} />}
                  {dirty && <span className="text-[10px] text-warnOrange">unsaved</span>}
                </div>
                <div className="mt-0.5 flex flex-wrap items-center gap-2 text-[10px] text-muted">
                  <ConventionBadge />
                  {!creating && <span>version {selectedCurve!.version}</span>}
                  {!creating && selectedCurve!.confirmedAt && <span>confirmed {formatTimestamp(selectedCurve!.confirmedAt)}</span>}
                </div>
              </div>
              {canWrite && !creating && selectedCurve?.status === "confirmed" && (
                <button type="button" disabled={saveMutation.isPending || confirmMutation.isPending} onClick={() => startNew(selectedCurve)} className="inline-flex h-8 items-center gap-1.5 rounded border border-border px-2.5 text-xs hover:border-accent hover:text-accent disabled:opacity-40">
                  <CopyPlus className="h-3.5 w-3.5" /> New draft from this
                </button>
              )}
              {editable && (
                <button
                  type="button"
                  disabled={Boolean(validationIssue) || saveMutation.isPending}
                  onClick={() => saveMutation.mutate()}
                  title={validationIssue ?? "Save full draft"}
                  className="ops-primary inline-flex h-8 items-center gap-1.5 rounded bg-accent px-3 text-xs font-medium text-white disabled:opacity-40"
                >
                  <Save className="h-3.5 w-3.5" /> {saveMutation.isPending ? "Saving…" : "Save draft"}
                </button>
              )}
              {canWrite && !creating && selectedCurve?.status === "draft" && (
                <button
                  type="button"
                  disabled={dirty || Boolean(validationIssue) || confirmMutation.isPending}
                  title={dirty ? "Save the draft before confirming it" : validationIssue ?? "Review this snapshot before confirming it"}
                  onClick={() => setConfirmReviewOpen(true)}
                  className="inline-flex h-8 items-center gap-1.5 rounded border border-posGreen/60 px-3 text-xs text-posGreen hover:bg-posGreen/10 disabled:opacity-40"
                >
                  <CheckCircle2 className="h-3.5 w-3.5" /> Review &amp; confirm
                </button>
              )}
            </div>

            {notice && (
              <div className={cn(
                "flex items-center border-b px-4 py-2 text-[11px]",
                notice.tone === "error" ? "border-negRed/30 bg-negRed/5 text-negRed" : notice.tone === "ok" ? "border-posGreen/30 bg-posGreen/5 text-posGreen" : "border-border/70 text-muted"
              )}>
                {notice.text}
                <button type="button" onClick={() => setNotice(null)} className="ml-auto">×</button>
              </div>
            )}

            {!canWrite && <div className="border-b border-border/70 bg-border/10 px-4 py-2 text-[11px] text-muted">Read-only for your role.</div>}

            <fieldset disabled={!editable || saveMutation.isPending || confirmMutation.isPending} className="ops-fx-editor-body space-y-4 p-4 disabled:opacity-75">
              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-[12rem_minmax(14rem,1fr)_minmax(18rem,2fr)]">
                <FormField label="Curve date / as-of">
                  <input type="date" value={form.curveDate} onChange={(event) => edit("curveDate", event.target.value)} className="fx-input" />
                </FormField>
                <FormField label="Source">
                  <input value={form.source} maxLength={240} placeholder="e.g. bank executable forwards" onChange={(event) => edit("source", event.target.value)} className="fx-input" />
                </FormField>
                <FormField label="Notes">
                  <input value={form.notes} maxLength={2000} placeholder="Optional methodology or context" onChange={(event) => edit("notes", event.target.value)} className="fx-input" />
                </FormField>
              </div>

              {editable && comparison && (
                <CurveReadinessStrip
                  comparison={comparison}
                  pointCount={form.points.length}
                  activeCurve={activeCurveQuery.data ?? null}
                  loadingActive={activeCurveQuery.isLoading}
                />
              )}

              <div className="ops-table-frame overflow-hidden rounded border border-border/70">
                <div className="flex items-center gap-2 border-b border-border/70 bg-border/10 px-3 py-2">
                  <div>
                    <div className="text-xs font-semibold">Forward points</div>
                    <div className="text-[10px] text-muted">All rates are EUR/RON (RON per EUR). Mid is required; bid and ask are optional. Interpolated points are reviewed and frozen with their anchors.</div>
                  </div>
                  {editable && comparison && comparison.missingMonths.length > 0 && (
                    <button
                      type="button"
                      onClick={() => {
                        const before = pointsOf(form);
                        const filled = fillMissingFxForwardMonths(before);
                        if (filled.length > MAX_CURVE_POINTS) {
                          setNotice({ tone: "error", text: `Gap fill would exceed the ${MAX_CURVE_POINTS}-point curve limit.` });
                          return;
                        }
                        const beforeMonths = new Set(before.map((point) => point.deliveryMonth));
                        const inserted = filled.filter(
                          (point) => point.derivation && !beforeMonths.has(point.deliveryMonth)
                        ).length;
                        edit("points", filled.map(pointFormOf));
                        setNotice({
                          tone: inserted > 0 ? "ok" : "muted",
                          text: inserted > 0
                            ? `Filled ${inserted} internal missing month${inserted === 1 ? "" : "s"}. Review and save the draft before confirmation.`
                            : "Only gaps of one or two months between exact points can be filled automatically.",
                        });
                      }}
                      className="ml-auto inline-flex items-center gap-1 rounded border border-warnOrange/50 px-2 py-1 text-[11px] text-warnOrange hover:bg-warnOrange/10"
                    >
                      <WandSparkles className="h-3 w-3" /> Fill internal gaps
                    </button>
                  )}
                  {editable && (
                    <button
                      type="button"
                      onClick={() => edit("points", [...form.points, { deliveryMonth: nextPointMonth(form.points), bid: "", mid: "", ask: "" }])}
                      className={cn("inline-flex items-center gap-1 text-[11px] text-accent hover:underline", (!comparison || comparison.missingMonths.length === 0) && "ml-auto")}
                    >
                      <Plus className="h-3 w-3" /> Add month
                    </button>
                  )}
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[36rem] text-xs">
                    <thead className="bg-border/10 text-[10px] uppercase tracking-wide text-muted">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium">Delivery month / origin</th>
                        <th className="px-3 py-2 text-right font-medium">Bid</th>
                        <th className="px-3 py-2 text-right font-medium">Mid</th>
                        <th className="px-3 py-2 text-right font-medium">Ask</th>
                        <th className="w-10 px-2 py-2"><span className="sr-only">Actions</span></th>
                      </tr>
                    </thead>
                    <tbody>
                      {form.points.map((point, index) => (
                        <tr key={`${point.deliveryMonth}-${index}`} className="border-t border-border/50">
                          <td className="px-3 py-1.5">
                            <input type="month" value={point.deliveryMonth} onChange={(event) => editPoint(index, "deliveryMonth", event.target.value)} className="fx-cell-input text-left" />
                            {point.derivation && (
                              <div
                                className="mt-1 w-fit rounded bg-warnOrange/10 px-1.5 py-0.5 text-[9px] font-medium uppercase text-warnOrange"
                                title={`${point.derivation.method}: ${point.derivation.leftAnchor.deliveryMonth} → ${point.derivation.rightAnchor.deliveryMonth}; weight ${point.derivation.weight}`}
                              >
                                Interpolated
                              </div>
                            )}
                          </td>
                          <td className="px-3 py-1.5"><input inputMode="decimal" value={point.bid} placeholder="—" onChange={(event) => editPoint(index, "bid", event.target.value)} className="fx-cell-input" /></td>
                          <td className="px-3 py-1.5"><input inputMode="decimal" value={point.mid} placeholder="required" onChange={(event) => editPoint(index, "mid", event.target.value)} className="fx-cell-input" /></td>
                          <td className="px-3 py-1.5"><input inputMode="decimal" value={point.ask} placeholder="—" onChange={(event) => editPoint(index, "ask", event.target.value)} className="fx-cell-input" /></td>
                          <td className="px-2 py-1.5 text-center">
                            {editable && <button type="button" title="Remove point" onClick={() => edit("points", removePointAndDependants(form.points, index))} className="rounded p-1 text-muted hover:bg-negRed/10 hover:text-negRed"><Trash2 className="h-3.5 w-3.5" /></button>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {editable && (
                <details className="ops-fx-paste rounded border border-border/70 p-3">
                  <summary className="cursor-pointer text-xs font-medium">Paste or replace points</summary>
                  <div className="mt-2 grid gap-2 lg:grid-cols-[minmax(0,1fr)_auto]">
                    <textarea
                      value={paste}
                      onChange={(event) => { setPaste(event.target.value); setPasteErrors([]); }}
                      rows={5}
                      placeholder={"deliveryMonth\tbid\tmid\task\n2027-01\t5.1200\t5.1300\t5.1400\n2027-02\t5.1400\t5.1500\t5.1600"}
                      className="rounded border border-border/70 bg-transparent p-2 font-mono text-xs outline-none focus:border-accent"
                    />
                    <button
                      type="button"
                      disabled={!paste.trim()}
                      onClick={() => {
                        const parsed = parseFxCurvePaste(paste);
                        setPasteErrors(parsed.errors);
                        if (parsed.errors.length === 0 && parsed.points.length > 0) {
                          edit("points", parsed.points.map(pointFormOf));
                          setNotice({ tone: "muted", text: `Replaced the editor with ${parsed.points.length} pasted point${parsed.points.length === 1 ? "" : "s"}. Save to persist them.` });
                        }
                      }}
                      className="inline-flex h-9 items-center justify-center gap-1.5 self-start rounded border border-accent/50 px-3 text-xs text-accent hover:bg-accent/10 disabled:opacity-40"
                    >
                      <ClipboardPaste className="h-3.5 w-3.5" /> Replace points
                    </button>
                  </div>
                  {pasteErrors.length > 0 && <ul className="mt-2 list-disc space-y-0.5 pl-5 text-[11px] text-negRed">{pasteErrors.map((error) => <li key={error}>{error}</li>)}</ul>}
                </details>
              )}

              {validationIssue && <div className="text-[11px] text-negRed">{validationIssue}</div>}
            </fieldset>
            {confirmReviewOpen && selectedCurve && comparison && (
              <FxConfirmationDialog
                curve={selectedCurve}
                comparison={comparison}
                activeCurve={activeCurveQuery.data ?? null}
                activeCurveLoading={activeCurveQuery.isLoading}
                busy={confirmMutation.isPending}
                error={confirmMutation.isError ? mutationError(confirmMutation.error) : null}
                onClose={() => {
                  if (!confirmMutation.isPending) setConfirmReviewOpen(false);
                }}
                onConfirm={() => confirmMutation.mutate(selectedCurve)}
              />
            )}
          </>
        )}
      </section>
    </div>
  );
}

function CurveReadinessStrip({
  comparison,
  pointCount,
  activeCurve,
  loadingActive,
}: {
  comparison: FxCurveComparison;
  pointCount: number;
  activeCurve: FxCurveDetail | null;
  loadingActive: boolean;
}) {
  const gapCount = comparison.missingMonths.length;
  return (
    <div className="ops-fx-summary grid overflow-hidden rounded border border-border/70 bg-border/5 sm:grid-cols-3">
      <div className="flex items-center gap-2 border-b border-border/60 px-3 py-2 sm:border-b-0 sm:border-r">
        <LineChart className="h-4 w-4 shrink-0 text-accent" />
        <div>
          <div className="text-[10px] uppercase tracking-wide text-muted">Coverage</div>
          <div className="text-xs font-medium">
            {pointCount} point{pointCount === 1 ? "" : "s"}
            {comparison.firstMonth && comparison.lastMonth
              ? ` · ${comparison.firstMonth} to ${comparison.lastMonth}`
              : ""}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2 border-b border-border/60 px-3 py-2 sm:border-b-0 sm:border-r">
        {gapCount === 0 ? (
          <CheckCircle2 className="h-4 w-4 shrink-0 text-posGreen" />
        ) : (
          <AlertTriangle className="h-4 w-4 shrink-0 text-warnOrange" />
        )}
        <div>
          <div className="text-[10px] uppercase tracking-wide text-muted">Internal gaps</div>
          <div className={cn("text-xs font-medium", gapCount > 0 && "text-warnOrange")}>
            {gapCount === 0
              ? "Continuous monthly coverage"
              : `${gapCount} missing month${gapCount === 1 ? "" : "s"}`}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2 px-3 py-2">
        <Activity className="h-4 w-4 shrink-0 text-accent" />
        <div>
          <div className="text-[10px] uppercase tracking-wide text-muted">Versus active curve</div>
          <div className="text-xs font-medium">
            {loadingActive
              ? "Loading comparison…"
              : activeCurve
                ? `${comparison.changedMonths} changed of ${comparison.comparableMonths} comparable months`
                : "No confirmed baseline as of this date"}
          </div>
        </div>
      </div>
    </div>
  );
}

function FxConfirmationDialog({
  curve,
  comparison,
  activeCurve,
  activeCurveLoading,
  busy,
  error,
  onClose,
  onConfirm,
}: {
  curve: FxCurveDetail;
  comparison: FxCurveComparison;
  activeCurve: FxCurveDetail | null;
  activeCurveLoading: boolean;
  busy: boolean;
  error: string | null;
  onClose: () => void;
  onConfirm: () => void;
}) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    const previouslyFocused = document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        closeRef.current();
        return;
      }
      if (event.key !== "Tab") return;
      const focusable = [...(dialogRef.current?.querySelectorAll<HTMLElement>(
        'button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
      ) ?? [])].filter((element) => element.offsetParent !== null);
      if (focusable.length === 0) {
        event.preventDefault();
        dialogRef.current?.focus();
        return;
      }
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    window.requestAnimationFrame(() => dialogRef.current?.focus());
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
      previouslyFocused?.focus();
    };
  }, []);

  const gapCount = comparison.missingMonths.length;
  const interpolatedPoints = curve.points.filter((point) => point.derivation != null);
  return (
    <div
      className="fixed inset-0 z-[100] grid place-items-center bg-black/45 p-4 backdrop-blur-[1px]"
      role="dialog"
      aria-modal="true"
      aria-labelledby="fx-confirm-title"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !busy) onClose();
      }}
    >
      <div
        ref={dialogRef}
        tabIndex={-1}
        className="ops-fx-confirmation max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-lg border border-border bg-bg shadow-2xl outline-none"
      >
        <div className="sticky top-0 z-10 flex items-start gap-3 border-b border-border/70 bg-bg px-4 py-3">
          <div>
            <h2 id="fx-confirm-title" className="text-sm font-semibold">Confirm FX curve snapshot</h2>
            <p className="mt-0.5 text-[10px] text-muted">
              {curve.curveDate} · version {curve.version} · {curve.source}
            </p>
          </div>
          <button
            type="button"
            disabled={busy}
            onClick={onClose}
            aria-label="Close confirmation review"
            className="ml-auto rounded p-1 text-muted hover:bg-accent/10 hover:text-fg disabled:opacity-40"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-4 p-4">
          <div className="grid overflow-hidden rounded border border-border/70 sm:grid-cols-5">
            <ReviewMetric label="Points" value={String(curve.points.length)} />
            <ReviewMetric
              label="Coverage"
              value={comparison.firstMonth && comparison.lastMonth
                ? `${comparison.firstMonth} → ${comparison.lastMonth}`
                : "—"}
            />
            <ReviewMetric
              label="Internal gaps"
              value={gapCount === 0 ? "None" : String(gapCount)}
              tone={gapCount === 0 ? "ok" : "warn"}
            />
            <ReviewMetric
              label="Interpolated"
              value={String(interpolatedPoints.length)}
              tone={interpolatedPoints.length > 0 ? "warn" : "default"}
            />
            <ReviewMetric
              label="Changed months"
              value={activeCurveLoading
                ? "Loading…"
                : activeCurve
                  ? `${comparison.changedMonths} / ${comparison.comparableMonths}`
                  : "No baseline"}
            />
          </div>

          {gapCount > 0 && (
            <div className="flex items-start gap-2 rounded border border-warnOrange/40 bg-warnOrange/5 p-3 text-xs text-warnOrange">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <div>
                <div className="font-medium">This snapshot has internal coverage gaps.</div>
                <div className="mt-0.5 text-[11px]">
                  Missing {comparison.missingMonths.slice(0, 12).join(", ")}
                  {comparison.missingMonths.length > 12
                    ? ` and ${comparison.missingMonths.length - 12} more`
                    : ""}. Missing months will remain valuation-incomplete; the resolver will not borrow from an older curve.
                </div>
              </div>
            </div>
          )}

          {interpolatedPoints.length > 0 && (
            <div className="rounded border border-warnOrange/40 bg-warnOrange/5 p-3 text-xs text-warnOrange">
              <div className="flex items-start gap-2">
                <WandSparkles className="mt-0.5 h-4 w-4 shrink-0" />
                <div>
                  <div className="font-medium">This snapshot includes reviewed interpolated points.</div>
                  <div className="mt-1 space-y-0.5 text-[11px]">
                    {interpolatedPoints.map((point) => (
                      <div key={point.deliveryMonth}>
                        <span className="font-mono">{point.deliveryMonth} {formatRate(point.mid)}</span>
                        {point.derivation && (
                          <span> · {point.derivation.leftAnchor.deliveryMonth} → {point.derivation.rightAnchor.deliveryMonth} · weight {formatInterpolationWeight(point.derivation.weight)}</span>
                        )}
                      </div>
                    ))}
                  </div>
                  <div className="mt-1 text-[10px]">Confirmation freezes each value, both anchors and the interpolation method with the curve.</div>
                </div>
              </div>
            </div>
          )}

          {activeCurve && comparison.largestMoves.length > 0 && (
            <section className="ops-table-frame overflow-hidden rounded border border-border/70">
              <div className="border-b border-border/70 bg-border/10 px-3 py-2">
                <div className="text-xs font-semibold">Largest mid-rate changes</div>
                <div className="text-[10px] text-muted">
                  Compared with confirmed curve {activeCurve.curveDate} · version {activeCurve.version} · {activeCurve.source}
                </div>
              </div>
              <table className="w-full text-xs">
                <thead className="text-[10px] uppercase tracking-wide text-muted">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium">Delivery month</th>
                    <th className="px-3 py-2 text-right font-medium">Active</th>
                    <th className="px-3 py-2 text-right font-medium">Draft</th>
                    <th className="px-3 py-2 text-right font-medium">Change</th>
                  </tr>
                </thead>
                <tbody>
                  {comparison.largestMoves.map((move) => (
                    <tr key={move.deliveryMonth} className="border-t border-border/50">
                      <td className="px-3 py-2 font-mono">{move.deliveryMonth}</td>
                      <td className="px-3 py-2 text-right font-mono tabular-nums">{formatRate(move.previousMid)}</td>
                      <td className="px-3 py-2 text-right font-mono tabular-nums">{formatRate(move.nextMid)}</td>
                      <td className={cn(
                        "px-3 py-2 text-right font-mono tabular-nums",
                        move.delta > 0 ? "text-posGreen" : "text-negRed"
                      )}>
                        {formatSignedRate(move.delta)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}

          {activeCurve && comparison.comparableMonths > 0 && comparison.changedMonths === 0 && (
            <div className="rounded border border-border/70 bg-border/5 p-3 text-xs text-muted">
              All {comparison.comparableMonths} comparable mid rates match the active curve.
            </div>
          )}
          {!activeCurveLoading && !activeCurve && (
            <div className="rounded border border-border/70 bg-border/5 p-3 text-xs text-muted">
              No confirmed curve is available on or before {curve.curveDate}; this will become the first eligible snapshot for that date.
            </div>
          )}

          <div className="rounded border border-accent/30 bg-accent/5 p-3 text-xs text-muted">
            Confirmation freezes this header and every forward point. New Quotes may consume the snapshot; existing saved Quotes keep their previously frozen FX provenance.
          </div>
          {error && <div className="rounded border border-negRed/40 bg-negRed/5 p-3 text-xs text-negRed">{error}</div>}

          <div className="flex justify-end gap-2 border-t border-border/70 pt-3">
            <button
              type="button"
              disabled={busy}
              onClick={onClose}
              className="h-8 rounded border border-border px-3 text-xs text-muted hover:border-accent/60 hover:text-fg disabled:opacity-40"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={busy || activeCurveLoading}
              onClick={onConfirm}
              className="inline-flex h-8 items-center gap-1.5 rounded bg-posGreen px-3 text-xs font-medium text-white hover:brightness-105 disabled:opacity-40"
            >
              <CheckCircle2 className="h-3.5 w-3.5" />
              {busy ? "Confirming…" : "Confirm immutable snapshot"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ReviewMetric({
  label,
  value,
  tone = "default",
}: {
  label: string;
  value: string;
  tone?: "default" | "ok" | "warn";
}) {
  return (
    <div className="border-b border-border/60 px-3 py-2 last:border-b-0 sm:border-b-0 sm:border-r sm:last:border-r-0">
      <div className="text-[9px] uppercase tracking-wide text-muted">{label}</div>
      <div className={cn(
        "mt-0.5 text-xs font-medium",
        tone === "ok" && "text-posGreen",
        tone === "warn" && "text-warnOrange"
      )}>
        {value}
      </div>
    </div>
  );
}

function pointFormOf(point: FxForwardPoint): PointForm {
  return {
    deliveryMonth: point.deliveryMonth,
    bid: point.bid == null ? "" : String(point.bid),
    mid: String(point.mid),
    ask: point.ask == null ? "" : String(point.ask),
    ...(point.derivation ? { derivation: point.derivation } : {}),
  };
}

function pointsOf(form: CurveForm): FxForwardPoint[] {
  return form.points.map((point) => ({
    deliveryMonth: point.deliveryMonth,
    bid: optionalPositiveNumber(point.bid),
    mid: requiredPositiveNumber(point.mid),
    ask: optionalPositiveNumber(point.ask),
    ...(point.derivation ? { derivation: point.derivation } : {}),
  }));
}

function editPointAndInvalidateDependants(
  points: readonly PointForm[],
  index: number,
  key: PointEditableKey,
  value: string
): PointForm[] {
  const edited = points[index];
  if (!edited) return [...points];
  if (edited.derivation) {
    return points.flatMap((point, pointIndex) => {
      if (pointIndex === index) {
        return [{ ...point, [key]: value, derivation: undefined }];
      }
      if (
        point.derivation &&
        point.derivation.leftAnchor.deliveryMonth === edited.derivation!.leftAnchor.deliveryMonth &&
        point.derivation.rightAnchor.deliveryMonth === edited.derivation!.rightAnchor.deliveryMonth
      ) {
        return [];
      }
      return [point];
    });
  }
  return points.flatMap((point, pointIndex) => {
    if (
      pointIndex !== index &&
      point.derivation &&
      (point.derivation.leftAnchor.deliveryMonth === edited.deliveryMonth ||
        point.derivation.rightAnchor.deliveryMonth === edited.deliveryMonth)
    ) {
      return [];
    }
    return [pointIndex === index ? { ...point, [key]: value } : point];
  });
}

function removePointAndDependants(points: readonly PointForm[], index: number): PointForm[] {
  const removed = points[index];
  if (!removed) return [...points];
  return points.filter((point, pointIndex) => {
    if (pointIndex === index) return false;
    if (removed.derivation || !point.derivation) return true;
    return (
      point.derivation.leftAnchor.deliveryMonth !== removed.deliveryMonth &&
      point.derivation.rightAnchor.deliveryMonth !== removed.deliveryMonth
    );
  });
}

function curveFormIssue(form: CurveForm): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(form.curveDate)) return "Choose a valid curve date.";
  if (!form.source.trim()) return "Identify the curve source.";
  if (form.points.length === 0) return "Add at least one forward point.";
  if (form.points.length > MAX_CURVE_POINTS) return `A curve cannot exceed ${MAX_CURVE_POINTS} points.`;
  const months = new Set<string>();
  for (let index = 0; index < form.points.length; index += 1) {
    const point = form.points[index]!;
    if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(point.deliveryMonth)) return `Row ${index + 1}: choose a valid delivery month.`;
    if (months.has(point.deliveryMonth)) return `Delivery month ${point.deliveryMonth} appears more than once.`;
    months.add(point.deliveryMonth);
    const mid = numberOrNull(point.mid);
    const bid = numberOrNull(point.bid);
    const ask = numberOrNull(point.ask);
    if (mid == null || mid <= 0) return `${point.deliveryMonth}: mid must be a positive number.`;
    if (point.bid.trim() && (bid == null || bid <= 0)) return `${point.deliveryMonth}: bid must be blank or a positive number.`;
    if (point.ask.trim() && (ask == null || ask <= 0)) return `${point.deliveryMonth}: ask must be blank or a positive number.`;
    if (bid != null && bid > mid) return `${point.deliveryMonth}: bid cannot exceed mid.`;
    if (ask != null && ask < mid) return `${point.deliveryMonth}: ask cannot be below mid.`;
  }
  return null;
}

function numberOrNull(value: string): number | null {
  if (!value.trim()) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function optionalPositiveNumber(value: string): number | null {
  if (!value.trim()) return null;
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) throw new Error("FX rates must be positive numbers.");
  return parsed;
}

function requiredPositiveNumber(value: string): number {
  const parsed = optionalPositiveNumber(value);
  if (parsed == null) throw new Error("Every forward point requires a mid rate.");
  return parsed;
}

function nextPointMonth(points: PointForm[]): string {
  const valid = points.map((point) => point.deliveryMonth).filter((month) => /^\d{4}-\d{2}$/.test(month)).sort();
  return valid.length > 0 ? addMonth(valid[valid.length - 1]!, 1) : currentMonth();
}

function Actuals() {
  const [from, setFrom] = useState(() => dateDaysAgo(180));
  const [to, setTo] = useState(isoToday);
  const query = useQuery({
    queryKey: ["eex-fx-actuals", EUR_RON_PAIR, from, to],
    enabled: Boolean(from && to && from <= to),
    queryFn: async () => normalizeFxActuals(await api<unknown>(`/eex/fx/actuals?pair=${encodeURIComponent(EUR_RON_PAIR)}&from=${from}&to=${to}`)),
  });
  const rows = [...(query.data ?? [])].sort((a, b) => b.date.localeCompare(a.date));

  return (
    <section className="ops-panel ops-fx-actuals overflow-hidden rounded-md border border-border/70">
      <div className="ops-fx-section-heading flex flex-wrap items-end gap-3 border-b border-border/70 px-4 py-3">
        <div className="mr-auto">
          <div className="flex items-center gap-2"><h2 className="text-sm font-semibold">BNR historical actuals</h2><ConventionBadge /></div>
          <div className="mt-1 text-[10px] text-muted">Daily BNR reference rates. Completed delivery months resolve to month-end or the nearest prior publication within seven days.</div>
        </div>
        <FormField label="From"><input type="date" value={from} onChange={(event) => setFrom(event.target.value)} className="fx-input w-36" /></FormField>
        <FormField label="To"><input type="date" value={to} onChange={(event) => setTo(event.target.value)} className="fx-input w-36" /></FormField>
        <button type="button" onClick={() => void query.refetch()} className="inline-flex h-8 items-center gap-1.5 rounded border border-border px-2.5 text-xs hover:border-accent hover:text-accent"><RefreshCw className={cn("h-3.5 w-3.5", query.isFetching && "animate-spin")} />Refresh</button>
      </div>
      {from > to && <ErrorText>The start date must not be after the end date.</ErrorText>}
      {query.isLoading && <EmptyText>Loading BNR actuals…</EmptyText>}
      {query.isError && <ErrorText>{mutationError(query.error)}</ErrorText>}
      {!query.isLoading && !query.isError && rows.length === 0 && <EmptyText>No BNR rates were found in this range.</EmptyText>}
      {rows.length > 0 && (
        <div className="ops-fx-history-table max-h-[36rem] overflow-auto">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-bg text-[10px] uppercase tracking-wide text-muted"><tr><th className="px-4 py-2 text-left font-medium">Date</th><th className="px-4 py-2 text-right font-medium">EUR/RON</th><th className="px-4 py-2 text-left font-medium">Source</th></tr></thead>
            <tbody>{rows.map((row) => <tr key={row.date} className="border-t border-border/50"><td className="px-4 py-2 font-mono">{row.date}</td><td className="px-4 py-2 text-right font-mono font-medium tabular-nums">{formatRate(row.rate)}</td><td className="px-4 py-2">{row.source ?? "BNR"}</td></tr>)}</tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function EffectiveRates({ onManageCurves }: { onManageCurves: () => void }) {
  const [asOf, setAsOf] = useState(isoToday);
  const [fromMonth, setFromMonth] = useState(() => addMonth(currentMonth(), -3));
  const [toMonth, setToMonth] = useState(() => addMonth(currentMonth(), 12));
  const [selectedMonth, setSelectedMonth] = useState<string | null>(null);
  const query = useQuery({
    queryKey: ["eex-fx-effective", EUR_RON_PAIR, asOf, fromMonth, toMonth],
    enabled: Boolean(asOf && fromMonth && toMonth && fromMonth <= toMonth),
    queryFn: async () => normalizeFxEffective(await api<unknown>(`/eex/fx/effective?pair=${encodeURIComponent(EUR_RON_PAIR)}&asOf=${asOf}&fromMonth=${fromMonth}&toMonth=${toMonth}`)),
  });
  const latestCurveQuery = useQuery({
    queryKey: ["eex-fx-latest", EUR_RON_PAIR, asOf],
    enabled: Boolean(asOf),
    queryFn: () =>
      api<unknown>(
        `/eex/fx/curves/latest?pair=${encodeURIComponent(EUR_RON_PAIR)}&asOf=${asOf}`
      ).then(normalizeFxCurveDetail),
  });
  const rows = query.data ?? [];
  const selected = rows.find((row) => row.deliveryMonth === selectedMonth) ?? rows[0] ?? null;
  const resolvedCount = rows.filter((row) => row.status === "resolved").length;
  const actualCount = rows.filter((row) => row.status === "resolved" && row.basis === "actual").length;
  const forwardCount = rows.filter((row) => row.status === "resolved" && row.basis === "forward").length;
  const indicativeCount = rows.filter((row) => row.status === "resolved" && row.basis === "indicative").length;
  const missingCount = rows.length - resolvedCount;

  return (
    <section className="ops-panel ops-fx-effective overflow-hidden rounded-md border border-border/70 bg-bg">
      <div className="ops-fx-section-heading flex flex-wrap items-end gap-3 border-b border-border/70 px-4 py-3">
        <div className="mr-auto">
          <div className="flex items-center gap-2"><h2 className="text-sm font-semibold">Effective EUR/RON curve</h2><ConventionBadge /></div>
          <div className="mt-1 text-[10px] text-muted">The actual rate the platform will use for each delivery month, with its source and selection basis.</div>
        </div>
        <FormField label="As of"><input type="date" value={asOf} onChange={(event) => setAsOf(event.target.value)} className="fx-input w-36" /></FormField>
        <FormField label="From month"><input type="month" value={fromMonth} onChange={(event) => setFromMonth(event.target.value)} className="fx-input w-36" /></FormField>
        <FormField label="To month"><input type="month" value={toMonth} onChange={(event) => setToMonth(event.target.value)} className="fx-input w-36" /></FormField>
        <button type="button" onClick={() => { void query.refetch(); void latestCurveQuery.refetch(); }} className="inline-flex h-8 items-center gap-1.5 rounded border border-border px-2.5 text-xs hover:border-accent hover:text-accent"><RefreshCw className={cn("h-3.5 w-3.5", (query.isFetching || latestCurveQuery.isFetching) && "animate-spin")} />Refresh</button>
      </div>
      {fromMonth > toMonth && <ErrorText>The first delivery month must not be after the last.</ErrorText>}
      {query.isLoading && <EmptyText>Resolving effective rates…</EmptyText>}
      {query.isError && <ErrorText>{mutationError(query.error)}</ErrorText>}
      {!query.isLoading && !query.isError && rows.length === 0 && <EmptyText>No effective-rate rows were returned.</EmptyText>}
      {rows.length > 0 && (
        <>
          <div className="ops-fx-summary grid border-b border-border/70 sm:grid-cols-5">
            <OverviewMetric
              icon={<Database className="h-4 w-4 text-accent" />}
              label="Confirmed snapshot"
              value={latestCurveQuery.isLoading
                ? "Loading…"
                : latestCurveQuery.isError
                  ? "Unavailable"
                : latestCurveQuery.data
                  ? `${latestCurveQuery.data.curveDate} · v${latestCurveQuery.data.version}`
                  : "None available"}
              detail={latestCurveQuery.isError
                ? mutationError(latestCurveQuery.error)
                : latestCurveQuery.data?.source ?? `As of ${asOf}`}
              tone={latestCurveQuery.isError ? "error" : "default"}
            />
            <OverviewMetric
              icon={missingCount === 0
                ? <CheckCircle2 className="h-4 w-4 text-posGreen" />
                : <AlertTriangle className="h-4 w-4 text-negRed" />}
              label="Resolved coverage"
              value={`${resolvedCount} / ${rows.length} months`}
              detail={missingCount === 0 ? "Complete for this range" : `${missingCount} missing`}
              tone={missingCount === 0 ? "ok" : "error"}
            />
            <OverviewMetric
              icon={<Activity className="h-4 w-4 text-posGreen" />}
              label="Historical actuals"
              value={`${actualCount} month${actualCount === 1 ? "" : "s"}`}
              detail="BNR month-end or prior ≤ 7 days"
            />
            <OverviewMetric
              icon={<LineChart className="h-4 w-4 text-accent" />}
              label="Forward-valued"
              value={`${forwardCount} month${forwardCount === 1 ? "" : "s"}`}
              detail="Mid from one latest confirmed curve"
            />
            <OverviewMetric
              icon={<AlertTriangle className="h-4 w-4 text-warnOrange" />}
              label="Indicative"
              value={`${indicativeCount} month${indicativeCount === 1 ? "" : "s"}`}
              detail="Current-month latest BNR proxy ≤ 7 days"
            />
          </div>

          <div className="border-b border-border/70 p-4">
            <EffectiveCurveChart rows={rows} asOf={asOf} />
          </div>

          <div className="ops-fx-provenance-grid grid min-h-[24rem] lg:grid-cols-[minmax(0,1fr)_21rem]">
            <div className="min-w-0 overflow-x-auto">
              <table className="w-full min-w-[48rem] text-xs">
                <thead className="sticky top-0 bg-bg text-[10px] uppercase tracking-wide text-muted"><tr><th className="px-4 py-2 text-left font-medium">Delivery month</th><th className="px-4 py-2 text-right font-medium">Effective EUR/RON</th><th className="px-4 py-2 text-left font-medium">Basis</th><th className="px-4 py-2 text-left font-medium">Rate date</th><th className="px-4 py-2 text-left font-medium">Source / reason</th></tr></thead>
                <tbody>{rows.map((row) => (
                  <tr
                    key={row.deliveryMonth}
                    tabIndex={0}
                    aria-selected={selected?.deliveryMonth === row.deliveryMonth}
                    onClick={() => setSelectedMonth(row.deliveryMonth)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        setSelectedMonth(row.deliveryMonth);
                      }
                    }}
                    className={cn(
                      "cursor-pointer border-t border-border/50 outline-none hover:bg-border/10 focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-accent",
                      selected?.deliveryMonth === row.deliveryMonth && "bg-accent/5"
                    )}
                  >
                    <td className="px-4 py-2 font-mono font-medium">{row.deliveryMonth}</td>
                    <td className="px-4 py-2 text-right font-mono tabular-nums">{row.status === "resolved" ? formatRate(row.rate) : "—"}</td>
                    <td className="px-4 py-2"><span className={cn("rounded px-1.5 py-0.5 text-[10px] uppercase", effectiveBasisBadgeClass(row.basis))}>{row.basis}</span></td>
                    <td className="px-4 py-2 font-mono text-muted">{row.status === "resolved" ? row.rateDate ?? "—" : "—"}</td>
                    <td className={cn("px-4 py-2", row.status === "missing" && "text-negRed")}>{row.status === "resolved" ? row.source ?? "—" : humanReason(row.reason)}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
            <EffectiveRateInspector row={selected} asOf={asOf} onManageCurves={onManageCurves} />
          </div>
        </>
      )}
    </section>
  );
}

function OverviewMetric({
  icon,
  label,
  value,
  detail,
  tone = "default",
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  detail: string;
  tone?: "default" | "ok" | "error";
}) {
  return (
    <div className="flex items-start gap-2 border-b border-border/60 px-4 py-3 last:border-b-0 sm:border-b-0 sm:border-r sm:last:border-r-0">
      <div className="mt-0.5">{icon}</div>
      <div className="min-w-0">
        <div className="text-[9px] uppercase tracking-wide text-muted">{label}</div>
        <div className={cn(
          "mt-0.5 truncate text-xs font-semibold",
          tone === "ok" && "text-posGreen",
          tone === "error" && "text-negRed"
        )}>{value}</div>
        <div className="mt-0.5 truncate text-[10px] text-muted" title={detail}>{detail}</div>
      </div>
    </div>
  );
}

function EffectiveCurveChart({ rows, asOf }: { rows: FxEffectiveRow[]; asOf: string }) {
  const width = 900;
  const height = 190;
  const left = 54;
  const right = 20;
  const top = 18;
  const bottom = 34;
  const resolved = rows.filter((row): row is Extract<FxEffectiveRow, { status: "resolved" }> => row.status === "resolved");
  if (resolved.length === 0) {
    return <div className="grid h-40 place-items-center text-xs text-muted">No resolved rates to chart.</div>;
  }
  const rates = resolved.map((row) => row.rate);
  const rawMin = Math.min(...rates);
  const rawMax = Math.max(...rates);
  const padding = Math.max((rawMax - rawMin) * 0.15, rawMax * 0.002, 0.005);
  const min = rawMin - padding;
  const max = rawMax + padding;
  const innerWidth = width - left - right;
  const innerHeight = height - top - bottom;
  const xOf = (index: number) => left + (rows.length === 1 ? innerWidth / 2 : index * innerWidth / (rows.length - 1));
  const yOf = (rate: number) => top + ((max - rate) / (max - min)) * innerHeight;
  const plotted = rows.map((row, index) => row.status === "resolved"
    ? { row, x: xOf(index), y: yOf(row.rate) }
    : null);
  const segments: Array<{ basis: "actual" | "forward" | "indicative"; points: Array<{ x: number; y: number }> }> = [];
  let startNewSegment = true;
  plotted.forEach((point) => {
    if (!point) {
      startNewSegment = true;
      return;
    }
    const lastSegment = segments[segments.length - 1];
    if (startNewSegment || !lastSegment || lastSegment.basis !== point.row.basis) {
      segments.push({ basis: point.row.basis, points: [{ x: point.x, y: point.y }] });
    } else {
      lastSegment.points.push({ x: point.x, y: point.y });
    }
    startNewSegment = false;
  });
  const cutoffMonth = asOf.slice(0, 7);
  const cutoffIndex = rows.findIndex((row) => row.deliveryMonth >= cutoffMonth);
  const cutoffX = cutoffIndex >= 0 ? xOf(cutoffIndex) : null;

  return (
    <div>
      <div className="mb-2 flex flex-wrap items-center gap-4 text-[10px] text-muted">
        <span className="font-medium text-fg">Effective monthly EUR/RON</span>
        <span className="inline-flex items-center gap-1.5"><span className="h-0.5 w-4 bg-posGreen" />BNR actual</span>
        <span className="inline-flex items-center gap-1.5"><span className="h-0.5 w-4 bg-accent" />Confirmed forward mid</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-4 border-t-2 border-dashed border-warnOrange" />Indicative BNR spot</span>
        <span className="ml-auto">As of {asOf}</span>
      </div>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="h-48 w-full"
        role="img"
        aria-label={`Effective EUR/RON curve from ${rows[0]?.deliveryMonth ?? ""} to ${rows[rows.length - 1]?.deliveryMonth ?? ""}`}
      >
        {[0, 0.5, 1].map((position) => {
          const y = top + position * innerHeight;
          const value = max - position * (max - min);
          return (
            <g key={position}>
              <line x1={left} x2={width - right} y1={y} y2={y} stroke="rgb(var(--border))" strokeOpacity="0.55" />
              <text x={left - 8} y={y + 3} textAnchor="end" fill="rgb(var(--muted))" fontSize="10">{formatRate(value)}</text>
            </g>
          );
        })}
        {cutoffX != null && (
          <g>
            <line x1={cutoffX} x2={cutoffX} y1={top - 4} y2={height - bottom + 7} stroke="rgb(var(--muted))" strokeDasharray="4 4" strokeOpacity="0.65" />
            <text x={cutoffX + 5} y={top + 8} fill="rgb(var(--muted))" fontSize="9">current/future</text>
          </g>
        )}
        {segments.map((segment, index) => (
          <polyline
            key={`${segment.basis}-${index}`}
            points={segment.points.map((point) => `${point.x},${point.y}`).join(" ")}
            fill="none"
            stroke={effectiveBasisStroke(segment.basis)}
            strokeDasharray={segment.basis === "indicative" ? "5 4" : undefined}
            strokeWidth="2.5"
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        ))}
        {plotted.map((point, index) => point ? (
          <circle
            key={point.row.deliveryMonth}
            cx={point.x}
            cy={point.y}
            r="3"
            fill={effectiveBasisStroke(point.row.basis)}
          >
            <title>{point.row.deliveryMonth}: {formatRate(point.row.rate)} · {point.row.basis}</title>
          </circle>
        ) : (
          <g key={`missing-${rows[index]!.deliveryMonth}`}>
            <circle cx={xOf(index)} cy={height - bottom} r="3.5" fill="rgb(var(--neg-red))" />
            <title>{rows[index]!.deliveryMonth}: missing</title>
          </g>
        ))}
        <text x={left} y={height - 8} fill="rgb(var(--muted))" fontSize="10">{rows[0]?.deliveryMonth}</text>
        <text x={width - right} y={height - 8} textAnchor="end" fill="rgb(var(--muted))" fontSize="10">{rows[rows.length - 1]?.deliveryMonth}</text>
      </svg>
    </div>
  );
}

function EffectiveRateInspector({
  row,
  asOf,
  onManageCurves,
}: {
  row: FxEffectiveRow | null;
  asOf: string;
  onManageCurves: () => void;
}) {
  return (
    <aside className="ops-fx-provenance border-t border-border/70 bg-border/5 p-4 lg:border-l lg:border-t-0">
      {!row ? (
        <div className="text-xs text-muted">Select a delivery month to inspect its provenance.</div>
      ) : (
        <div className="space-y-4">
          <div>
            <div className="text-[9px] uppercase tracking-wide text-muted">Selected delivery month</div>
            <div className="mt-1 flex items-center gap-2">
              <span className="font-mono text-base font-semibold">{row.deliveryMonth}</span>
              <span className={cn("rounded px-1.5 py-0.5 text-[9px] uppercase", effectiveBasisBadgeClass(row.basis))}>{row.basis}</span>
            </div>
          </div>

          {row.status === "missing" ? (
            <div className="rounded border border-negRed/40 bg-negRed/5 p-3 text-xs text-negRed">
              <div className="flex items-center gap-1.5 font-medium"><AlertTriangle className="h-3.5 w-3.5" />Rate unavailable</div>
              <div className="mt-1 text-[11px]">{humanReason(row.reason)}</div>
              <div className="mt-2 text-[10px]">No substitute or older snapshot is used.</div>
            </div>
          ) : (
            <>
              <div>
                <div className="text-[9px] uppercase tracking-wide text-muted">Effective rate</div>
                <div className="mt-1 font-mono text-2xl font-semibold tabular-nums">{formatRate(row.rate)}</div>
                <div className="text-[10px] text-muted">RON paid for 1 EUR</div>
              </div>
              <dl className="space-y-2 text-xs">
                <InspectorRow label="Selection as of" value={asOf} mono />
                <InspectorRow label="Rate date" value={row.rateDate ?? "—"} mono />
                <InspectorRow label="Source" value={row.source ?? "—"} />
                {row.basis === "forward" ? (
                  <>
                    <InspectorRow label="Curve date" value={row.curveDate ?? "—"} mono />
                    <InspectorRow label="Curve version" value={row.curveVersion == null ? "—" : String(row.curveVersion)} mono />
                    <InspectorRow label="Confirmed" value={row.confirmedAt ? formatTimestamp(row.confirmedAt) : "—"} />
                    <InspectorRow label="Bid / mid / ask" value={`${row.bid == null ? "—" : formatRate(row.bid)} / ${formatRate(row.mid)} / ${row.ask == null ? "—" : formatRate(row.ask)}`} mono />
                    <InspectorRow label="Curve ID" value={row.curveId ?? "—"} mono wrap />
                  </>
                ) : row.basis === "indicative" ? (
                  <>
                    <InspectorRow label="Basis" value="Latest eligible BNR spot proxy" />
                    <InspectorRow label="Observation age" value={row.ageDays == null ? "—" : `${row.ageDays} calendar day${row.ageDays === 1 ? "" : "s"}`} />
                    <InspectorRow label="Freshness limit" value={row.maxAgeDays == null ? "—" : `${row.maxAgeDays} calendar days`} />
                    <InspectorRow label="Method" value={row.method && row.methodVersion != null ? `${row.method} · v${row.methodVersion}` : row.method ?? "—"} mono wrap />
                    <InspectorRow label="Forward fallback" value={row.fallbackReason ? humanReason(row.fallbackReason) : "—"} />
                    <InspectorRow label="Curve date" value={row.fallbackCurveDate ?? "—"} mono />
                    <InspectorRow label="Curve ID" value={row.fallbackCurveId ?? "—"} mono wrap />
                  </>
                ) : (
                  <>
                    <InspectorRow label="Month-end target" value={row.actualTargetDate ?? "—"} mono />
                    <InspectorRow label="Prior fallback" value={row.fallbackDays == null ? "—" : row.fallbackDays === 0 ? "Exact month-end" : `${row.fallbackDays} calendar day${row.fallbackDays === 1 ? "" : "s"}`} />
                  </>
                )}
              </dl>
            </>
          )}

          {row.basis !== "actual" && (
            <button type="button" onClick={onManageCurves} className="inline-flex h-8 w-full items-center justify-center gap-1.5 rounded border border-accent/50 text-xs text-accent hover:bg-accent/10">
              Manage curve snapshots <ArrowRight className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      )}
    </aside>
  );
}

function InspectorRow({
  label,
  value,
  mono = false,
  wrap = false,
}: {
  label: string;
  value: string;
  mono?: boolean;
  wrap?: boolean;
}) {
  return (
    <div className="grid grid-cols-[7rem_minmax(0,1fr)] gap-2 border-b border-border/50 pb-2 last:border-b-0">
      <dt className="text-muted">{label}</dt>
      <dd className={cn("text-right", mono && "font-mono", wrap && "break-all")}>{value}</dd>
    </div>
  );
}

function FormField({ label, children }: { label: string; children: React.ReactNode }) {
  return <label className="ops-fx-field flex min-w-0 flex-col gap-0.5 text-[10px] uppercase tracking-wide text-muted">{label}{children}</label>;
}

function StatusBadge({ status }: { status: "draft" | "confirmed" }) {
  return <span className={cn("rounded px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wide", status === "confirmed" ? "bg-posGreen/10 text-posGreen" : "bg-warnOrange/10 text-warnOrange")}>{status}</span>;
}

function EmptyText({ children }: { children: React.ReactNode }) {
  return <div className="p-4 text-xs text-muted">{children}</div>;
}

function ErrorText({ children }: { children: React.ReactNode }) {
  return <div className="border-b border-negRed/20 bg-negRed/5 p-4 text-xs text-negRed last:border-b-0">{children}</div>;
}

function mutationError(error: unknown): string {
  if (error instanceof ApiError) {
    const body = error.body && typeof error.body === "object" ? error.body as Record<string, unknown> : null;
    if (body?.code === "stale_version") return "This draft changed in another tab. Reload it before saving or confirming.";
    return error.message;
  }
  return error instanceof Error ? error.message : String(error);
}

function formatRate(value: number): string {
  return new Intl.NumberFormat("en-GB", { minimumFractionDigits: 4, maximumFractionDigits: 6 }).format(value);
}

function formatSignedRate(value: number): string {
  const formatted = new Intl.NumberFormat("en-GB", { minimumFractionDigits: 4, maximumFractionDigits: 6 }).format(value);
  return value > 0 ? `+${formatted}` : formatted;
}

function formatInterpolationWeight(value: number): string {
  return new Intl.NumberFormat("en-GB", {
    style: "percent",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(value);
}

function effectiveBasisBadgeClass(basis: FxEffectiveRow["basis"]): string {
  if (basis === "actual") return "bg-posGreen/10 text-posGreen";
  if (basis === "indicative") return "bg-warnOrange/10 text-warnOrange";
  return "bg-accent/10 text-accent";
}

function effectiveBasisStroke(basis: FxEffectiveRow["basis"]): string {
  if (basis === "actual") return "rgb(var(--pos-green))";
  if (basis === "indicative") return "rgb(var(--warn-orange))";
  return "rgb(var(--accent))";
}

function formatTimestamp(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("en-GB", { dateStyle: "medium", timeStyle: "short" }).format(date);
}

function dateDaysAgo(days: number): string {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

function humanReason(value: string): string {
  const labels: Record<string, string> = {
    no_confirmed_curve: "No confirmed curve is available as of this date.",
    curve_month_missing: "The latest confirmed curve has no point for this delivery month.",
    invalid_forward_point: "The forward point is invalid.",
    actual_missing: "No eligible BNR month-end or prior fixing is available.",
    invalid_actual_rate: "The eligible BNR actual is invalid.",
    indicative_rate_missing: "No BNR fixing is available for the current-month proxy.",
    indicative_rate_stale: "The latest BNR fixing is too old for the current-month proxy.",
    invalid_indicative_rate: "The latest BNR fixing is invalid.",
  };
  return labels[value] ?? value.replaceAll("_", " ");
}
